/**
 * ControlPanel — panel lateral de operación sobre el regulador seleccionado.
 *
 * Fase 2 de la integración MQTT: envía comandos de operación básica
 * (estado, plan, alarmas) vía `coloma-mqtt` y muestra el historial con el
 * resultado correlacionado por request_id.
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Power, Zap, BellRing, CheckCircle2, XCircle, Loader2, AlertTriangle } from 'lucide-react';
import { useSelection } from '../../store/selection';
import { useColomaMqtt } from '../../store/coloma-mqtt';
import { toCanonicalId, toDisplayId } from '../../utils/canonicalId';
import { resolveEstado } from '../../data/estados-regulador';
import { MatrizFasesBlock } from './MatrizFasesBlock';

const STATE_LABELS: Record<string, string> = { A: 'Apagado', I: 'Intermitente', H: 'Activo' };

/** Descripción legible de un comando para el historial. */
function describeCmd(cmd: string, params: Record<string, number | string>): string {
  switch (cmd) {
    case 'force_state': return `Estado → ${STATE_LABELS[String(params.state)] ?? params.state}`;
    case 'force_plan':  return `Plan → P${params.plan}`;
    case 'release_plan': return 'Liberar plan';
    case 'clear_alarms': return 'Borrar alarmas';
    default: return cmd;
  }
}

interface PendingCmd {
  cmd: string;
  params: Record<string, number | string>;
  label: string;
}

/** Sección con título e icono. */
function Section({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">
        {icon}{title}
      </div>
      {children}
    </div>
  );
}

/** Botón de comando. */
function CmdButton({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="px-2 py-1.5 rounded text-[11px] font-medium bg-gray-800 text-gray-200 border border-gray-700
                 hover:bg-gray-700 hover:border-cyan-700/60 transition-colors
                 disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {label}
    </button>
  );
}

export function ControlPanel() {
  const selectedId = useSelection(s => s.selectedId);
  const clear = useSelection(s => s.clear);
  const canonicalId = selectedId !== null ? toCanonicalId(selectedId) : '';
  const status = useColomaMqtt(s => (canonicalId ? s.reguladores[canonicalId] : undefined));
  const commandLog = useColomaMqtt(s => s.commandLog);
  const sendCommand = useColomaMqtt(s => s.sendCommand);
  const [confirm, setConfirm] = useState<PendingCmd | null>(null);

  if (selectedId === null) return null;

  const online = status?.online ?? false;
  const estado = resolveEstado(status?.estado, online);
  const log = commandLog.filter(e => e.canonicalId === canonicalId);

  const ask = (cmd: string, params: Record<string, number | string>, label: string) =>
    setConfirm({ cmd, params, label });
  const execute = () => {
    if (confirm) sendCommand(canonicalId, confirm.cmd, confirm.params);
    setConfirm(null);
  };

  return (
    <motion.div
      initial={{ x: 340, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="absolute top-0 right-0 bottom-0 w-[340px] bg-gray-950/95 backdrop-blur-md
                 border-l border-gray-700/60 z-40 flex flex-col shadow-2xl"
    >
      {/* Cabecera */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
        <div>
          <div className="text-sm font-semibold text-white font-mono">{toDisplayId(selectedId)}</div>
          <div className="text-[10px] text-gray-500 font-mono">{canonicalId} · {status?.nombre ?? '—'}</div>
        </div>
        <button
          onClick={clear}
          className="p-1.5 rounded hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
          title="Cerrar"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Estado actual */}
      <div className="px-4 py-3 border-b border-gray-800 flex items-center gap-3">
        <span
          className="px-2 py-0.5 rounded-full text-[11px] font-medium text-white"
          style={{ backgroundColor: estado.color }}
        >
          {estado.label}
        </span>
        {online && (
          <span className="text-[11px] text-gray-400 font-mono">
            Plan {status?.plan ?? '—'} · Fase {status?.fase ?? '—'}
          </span>
        )}
      </div>

      {/* Estructura de fases — detalle de ciclo (cuando el regulador lo reporta) */}
      {online && status?.matriz_fases && status.matriz_fases.length > 0 && (
        <MatrizFasesBlock
          matriz={status.matriz_fases}
          faseActual={status.fase ?? null}
          tiempos={status.tiempos_fase}
        />
      )}

      {/* Cuerpo */}
      <div className="flex-1 overflow-y-auto">
        {!online ? (
          <div className="px-4 py-6 text-center text-xs text-gray-500">
            Regulador desconectado.<br />No se pueden enviar comandos.
          </div>
        ) : (
          <div className="p-4 space-y-4">
            <Section title="Estado" icon={<Power className="w-3 h-3" />}>
              <div className="grid grid-cols-3 gap-1.5">
                <CmdButton label="Apagado" onClick={() => ask('force_state', { state: 'A' }, 'Apagar regulador')} />
                <CmdButton label="Intermit." onClick={() => ask('force_state', { state: 'I' }, 'Poner en intermitente')} />
                <CmdButton label="Activo" onClick={() => ask('force_state', { state: 'H' }, 'Activar regulación')} />
              </div>
            </Section>

            <Section title="Plan" icon={<Zap className="w-3 h-3" />}>
              <div className="grid grid-cols-4 gap-1.5">
                {[1, 2, 3, 4].map(p => (
                  <CmdButton key={p} label={`P${p}`} onClick={() => ask('force_plan', { plan: p }, `Forzar plan ${p}`)} />
                ))}
              </div>
              <button
                onClick={() => ask('release_plan', {}, 'Liberar plan (volver a horario)')}
                className="mt-1.5 w-full px-2 py-1.5 rounded text-[11px] font-medium bg-gray-800 text-gray-300
                           border border-gray-700 hover:bg-gray-700 transition-colors"
              >
                Liberar plan
              </button>
            </Section>

            <Section title="Alarmas" icon={<BellRing className="w-3 h-3" />}>
              <button
                onClick={() => ask('clear_alarms', {}, 'Borrar alarmas')}
                className="w-full px-2 py-1.5 rounded text-[11px] font-medium bg-gray-800 text-gray-300
                           border border-gray-700 hover:bg-gray-700 transition-colors"
              >
                Borrar alarmas
              </button>
            </Section>
          </div>
        )}

        {/* Historial de comandos */}
        <div className="border-t border-gray-800 p-4">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-2">Historial</div>
          {log.length === 0 ? (
            <div className="text-[11px] text-gray-600">Sin comandos enviados.</div>
          ) : (
            <div className="space-y-1.5">
              {log.slice(0, 12).map(e => (
                <div key={e.id} className="flex items-center gap-2 text-[11px]">
                  {e.status === 'pending' && <Loader2 className="w-3 h-3 text-gray-400 animate-spin flex-shrink-0" />}
                  {e.status === 'success' && <CheckCircle2 className="w-3 h-3 text-green-400 flex-shrink-0" />}
                  {e.status === 'error' && <XCircle className="w-3 h-3 text-red-400 flex-shrink-0" />}
                  <span className="text-gray-500 font-mono flex-shrink-0">
                    {new Date(e.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </span>
                  <span className="text-gray-300 flex-1 truncate">{describeCmd(e.cmd, e.params)}</span>
                  {e.error && <span className="text-red-400 truncate max-w-[80px]" title={e.error}>{e.error}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmación */}
      <AnimatePresence>
        {confirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
          >
            <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 w-full">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-medium text-white">Confirmar comando</span>
              </div>
              <p className="text-xs text-gray-400 mb-4">
                {confirm.label} en <span className="font-mono text-cyan-400">{canonicalId}</span>.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirm(null)}
                  className="flex-1 px-3 py-1.5 rounded text-xs font-medium bg-gray-800 text-gray-300
                             hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={execute}
                  className="flex-1 px-3 py-1.5 rounded text-xs font-medium bg-cyan-600 text-white
                             hover:bg-cyan-500 transition-colors"
                >
                  Ejecutar
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
